import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  FaComments,
  FaLightbulb,
  FaLanguage,
  FaEllipsis,
} from "react-icons/fa6";
import { Link } from "react-router-dom"; // Importa Link para navegación interna

const FAQ: React.FC = () => {
  const { t, ready } = useTranslation();
  const [activeSection, setActiveSection] = useState<string>("Interpretations");
  const [expandedSections, setExpandedSections] = useState<{
    [key: string]: boolean;
  }>({ Interpretations: true });
  const [expandedQuestions, setExpandedQuestions] = useState<{
    [key: string]: Set<number>;
  }>({ Interpretations: new Set<number>() });

  const sections = [
    { key: "Interpretations", icon: <FaComments /> },
    { key: "Expertise", icon: <FaLightbulb /> },
    { key: "Languages", icon: <FaLanguage /> },
    { key: "Others", icon: <FaEllipsis /> },
  ];

  const faqsData: {
    [key: string]: { question: string; answer: React.ReactNode }[];
  } = {
    Interpretations: [
      {
        question: t('faq.questions.interpretationWhat.q'),
        answer: t('faq.questions.interpretationWhat.a'),
      },
      {
        question: t('faq.questions.simultaneousVsConsecutive.q'),
        answer: t('faq.questions.simultaneousVsConsecutive.a'),
      },
      {
        question: t('faq.questions.servicesOffered.q'),
        answer: t('faq.questions.servicesOffered.a'),
      },
    ],
    Expertise: [
      {
        question: t('faq.questions.industriesServed.q'),
        answer: t('faq.questions.industriesServed.a'),
      },
      {
        question: t('faq.questions.interpretersCertified.q'),
        answer: t('faq.questions.interpretersCertified.a'),
      },
      {
        question: t('faq.questions.immigrationInterviews.q'),
        answer: t('faq.questions.immigrationInterviews.a'),
      },
    ],
    Languages: [
      {
        question: t('faq.questions.languagesAvailable.q'),
        answer: t('faq.questions.languagesAvailable.a'),
      },
    ],
    Others: [
      {
        question: t('faq.others.q1'),
        answer: t('faq.others.paymentMethods'),
      },
      {
        question: t('faq.others.q2'),
        answer: t('faq.others.paymentSecurity'),
      },
      {
        question: t('faq.others.q3'),
        answer: t('faq.others.confidentiality'),
      },
      {
        question: t('faq.others.q4'),
        answer: t('faq.others.contracts'),
      },
    ],
  };

  useEffect(() => {
    const isDesktop = window.innerWidth >= 768;
    if (isDesktop) {
      setActiveSection("Interpretations");
      setExpandedQuestions({ Interpretations: new Set<number>() });
    }
  }, []);

  const selectSection = (sectionKey: string) => {
    setActiveSection(sectionKey);
    setExpandedQuestions((prev) => ({
      ...prev,
      [sectionKey]: new Set<number>(),
    }));
  };

  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  };

  const toggleQuestion = (index: number) => {
    setExpandedQuestions((prev) => {
      const currentSection = prev[activeSection] || new Set<number>();
      const newSection = new Set(currentSection);
      
      if (newSection.has(index)) {
        newSection.delete(index);
      } else {
        newSection.add(index);
      }
      
      return {
        ...prev,
        [activeSection]: newSection,
      };
    });
  };

  if (!ready) return null;

  return (
    <div className="px-6 py-2">
      <div className="container mx-auto mt-4 lg:mt-12 px-8 py-16 bg-white rounded-lg shadow-lg">
        <h2 className="text-3xl text-[#024772] font-bold text-gray-800 -mt-10 mb-2 text-center">
          {t('faq.title')}
        </h2>

        {/* Mobile View */}
        <div className="block lg:hidden space-y-4">
          {sections.map((section) => (
            <div key={section.key}>
              <button
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-lg font-semibold ${
                  activeSection === section.key
                    ? "bg-blue-100 text-[#024772]"
                    : "bg-gray-100 text-gray-500"
                }`}
                onClick={() => toggleSection(section.key)}
              >
                <div className="flex items-center space-x-3">
                  <span className="text-xl">{section.icon}</span>
                  <span>{t(`faq.sections.${section.key}`)}</span>
                </div>
                <span>{expandedSections[section.key] ? "−" : "+"}</span>
              </button>

              {activeSection === section.key && faqsData[section.key] && (
                <div className="mt-4 space-y-4 px-4 py-2 bg-gray-50 rounded-lg">
                  {faqsData[section.key].map((faq, index) => (
                    <div key={index}>
                      <button
                        className="text-lg font-medium text-left w-full text-gray-800 hover:text-blue-600 focus:outline-none"
                        onClick={() => toggleQuestion(index)}
                      >
                        {faq.question}
                        <span className="float-right">
                          {expandedQuestions[section.key]?.has(index) ? "−" : "+"}
                        </span>
                      </button>
                      {expandedQuestions[section.key]?.has(index) && (
                        <div className="text-gray-600 mt-2 pl-2">
                          {faq.answer}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Desktop View */}
        <div className="hidden lg:flex mt-10">
          <div className="w-1/4">
            <nav className="space-y-4">
              {sections.map((section) => (
                <button
                  key={section.key}
                  onClick={() => selectSection(section.key)}
                  className={`flex items-center space-x-3 w-full px-4 py-2 rounded-lg ${
                    activeSection === section.key
                      ? "bg-blue-100 text-[#024772] font-semibold"
                      : "text-gray-600"
                  } hover:bg-blue-50`}
                >
                  <span className="text-xl">{section.icon}</span>
                  <span>{t(`faq.sections.${section.key}`)}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="w-3/4 pl-8">
            {activeSection && faqsData[activeSection] && (
              <div>
                <h3 className="text-2xl text-[#024772] font-bold mb-4">
                  {t(`faq.sections.${activeSection}`)}
                </h3>
                <div className="space-y-4">
                  {faqsData[activeSection].map((faq, index) => (
                    <div key={index}>
                      <button
                        className="text-lg font-semibold text-left w-full text-gray-800 hover:text-blue-600 focus:outline-none"
                        onClick={() => toggleQuestion(index)}
                      >
                        {faq.question}
                        <span className="float-right">
                          {expandedQuestions[activeSection]?.has(index) ? "−" : "+"}
                        </span>
                      </button>
                      <div
                        className={`overflow-hidden transition-max-height duration-300 ease-in-out ${
                          expandedQuestions[activeSection]?.has(index) ? "max-h-screen" : "max-h-0"
                        }`}
                      >
                        {expandedQuestions[activeSection]?.has(index) && (
                          <div className="text-gray-600 mt-2 transition-opacity duration-300 ease-in-out">
                            {faq.answer}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
